import { useCallback, useRef } from "react";

const MOVE_CANCEL_PX = 12;

export function useLongPress(onLongPress, { delay = 480 } = {}) {
    const timerRef = useRef(null);
    const triggeredRef = useRef(false);
    const suppressClickRef = useRef(false);
    const startPointRef = useRef(null);
    const dataRef = useRef(null);

    const cancel = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        startPointRef.current = null;
    }, []);

    const shouldCancelForMove = useCallback((clientX, clientY) => {
        const start = startPointRef.current;
        if (!start) {
            return false;
        }
        return Math.hypot(clientX - start.x, clientY - start.y) > MOVE_CANCEL_PX;
    }, []);

    const bind = useCallback(
        (data) => ({
            onTouchStart: (event) => {
                const touch = event.touches[0];
                triggeredRef.current = false;
                suppressClickRef.current = false;
                dataRef.current = data;
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                startPointRef.current = touch
                    ? { x: touch.clientX, y: touch.clientY }
                    : null;
                timerRef.current = setTimeout(() => {
                    triggeredRef.current = true;
                    suppressClickRef.current = true;
                    onLongPress(data, event);
                }, delay);
            },
            onTouchMove: (event) => {
                const touch = event.touches[0];
                if (touch && shouldCancelForMove(touch.clientX, touch.clientY)) {
                    cancel();
                }
            },
            onTouchEnd: cancel,
            onTouchCancel: cancel,
            onMouseDown: (event) => {
                if (event.button !== 0) {
                    return;
                }
                triggeredRef.current = false;
                suppressClickRef.current = false;
                dataRef.current = data;
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                startPointRef.current = { x: event.clientX, y: event.clientY };
                timerRef.current = setTimeout(() => {
                    triggeredRef.current = true;
                    suppressClickRef.current = true;
                    onLongPress(data, event);
                }, delay);
            },
            onMouseMove: (event) => {
                if (shouldCancelForMove(event.clientX, event.clientY)) {
                    cancel();
                }
            },
            onMouseUp: cancel,
            onMouseLeave: cancel,
            onContextMenu: (event) => {
                event.preventDefault();
                triggeredRef.current = true;
                suppressClickRef.current = true;
                onLongPress(data, event);
            },
            onClick: (event) => {
                if (suppressClickRef.current) {
                    suppressClickRef.current = false;
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
        }),
        [cancel, delay, onLongPress, shouldCancelForMove]
    );

    const consumeIfTriggered = useCallback(() => {
        if (triggeredRef.current) {
            triggeredRef.current = false;
            suppressClickRef.current = false;
            return true;
        }
        return false;
    }, []);

    return { bind, consumeIfTriggered };
}
